import { useState, useCallback, useEffect } from "react"

/**
 * A hook for working with a single URL hash parameter.
 *
 * Similar to useState, but the value is persisted in the URL hash.
 *
 * == Example
 *  const [tab, setTab, tabHref] = useHashParam("tab")
 *
 *  // tab is the current value
 *  // setTab("profile") updates the hash to include tab=profile
 *  // setTab(null) removes the param from the hash
 *  // tabHref("profile") returns "#tab=profile&..." preserving other params
 *
 * @param {string} key - The hash param key
 */
export function useHashParam(key) {
  const getHashParam = useCallback(() => {
    const hash = window.location.hash.slice(1)
    const params = new URLSearchParams(hash)
    return params.get(key)
  }, [key])

  const [value, setValue] = useState(() => getHashParam())

  useEffect(() => {
    const handleHashChange = () => {
      setValue(getHashParam())
    }

    window.addEventListener("hashchange", handleHashChange)
    return () => window.removeEventListener("hashchange", handleHashChange)
  }, [getHashParam])

  const setHashParam = useCallback(
    newValue => {
      const hash = window.location.hash.slice(1)
      const params = new URLSearchParams(hash)

      if (newValue === null || newValue === undefined) {
        params.delete(key)
      } else {
        params.set(key, newValue)
      }

      window.location.hash = params.toString()
      setValue(newValue)
    },
    [key]
  )

  const getHref = useCallback(
    newValue => {
      const hash = window.location.hash.slice(1)
      const params = new URLSearchParams(hash)

      if (newValue === null || newValue === undefined) {
        params.delete(key)
      } else {
        params.set(key, newValue)
      }

      const hashString = params.toString()
      return hashString ? `#${hashString}` : "#"
    },
    [key]
  )

  return [value, setHashParam, getHref]
}
